import { Box } from "@mui/system";
import Tab from "@mui/material/Tab";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import TabPanel from "@mui/lab/TabPanel";
import Allergies from "../Forms/Allergies";
import CriticNotes from "../Forms/CriticNotes";
import PatientBody from "../Forms/PatientBody";
import { TabContext, TabList } from "@mui/lab";
import useStyles from "../../../../styles/styles";
import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router";
import { configState } from "../../../../recoil/atoms";
import ChronicDiseases from "../Forms/ChronicDiseases";
import { editValidationSchema } from "../FormModel/validationSchema";
import MainCard from "../../../../styles/ui-component/cards/MainCard";
import { Button, Grid, LinearProgress, Typography } from "@mui/material";
import { EditPatientRequest, GetPatientResponse, PatientApi } from "../../../../api/medical-operations";

type LocationState = {
  selectedTab?: string;
};

const EditPatient = () => {
  const classes = useStyles();
  let { patientID } = useParams();
  const location = useLocation();
  const config = useRecoilValue(configState);
  const state = location.state as LocationState;

  const initialSelectedTab = state?.selectedTab || "0";
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [patient, setPatient] = useState<GetPatientResponse>();
  const [selectedTab, setSelectedTab] = useState(initialSelectedTab);

  const getPatient = () => {
    const patient = new PatientApi(config);
    patient
      .getTenantPatientById(patientID!)
      .then((res) => {
        setIsLoading(false);
        setPatient(res.data!);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  useEffect(() => {
    getPatient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state?.selectedTab) {
      setSelectedTab(state.selectedTab);
    }
  }, [state?.selectedTab]);

  const initialPatientValues = {
    Patient: {
      Allergies: patient?.Allergies || [],
      ChronicDiseases: patient?.ChronicDiseases || [],
      CriticNotes: patient?.CriticNotes || [],
      Height: patient?.Height || "",
      Weight: patient?.Weight || "",
    },
  };

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const editPatient = (values: EditPatientRequest) => {
    const editPatient = new PatientApi(config);
    editPatient
      .editTenantPatient(patientID!, values)
      .then((res) => {
        toast.success(res?.data?.Message!);        
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  const handleSubmit = (values: any, actions: any) => {
    actions.setTouched({});
    actions.setSubmitting(false);    
        
    const formattedCriticNotes = values.Patient.CriticNotes.map((note: any) => ({
      DateCreated: note.DateCreated,
      DateModified: note.DateModified,
      Note: note.Note,
      NoteNumber: note.NoteNumber,
    }));
  
    const valuesEdited: EditPatientRequest = {
      Allergies: values?.Patient?.Allergies,
      ChronicDiseases: values?.Patient?.ChronicDiseases,
      CriticNotes: formattedCriticNotes,
      Height: values?.Patient?.Height,
      Weight: values?.Patient?.Weight,
    };  
    editPatient(valuesEdited);
  };
  
  return (
    <>
      <MainCard>
        {!isLoading ? (
          <Formik
            initialValues={initialPatientValues!}
            enableReinitialize={true}
            validationSchema={editValidationSchema[4]}
            onSubmit={handleSubmit}
          >
            {({ dirty }) => (
              <Form>
                <Box sx={{ width: "100%" }}>
                  <Box sx={{ bgcolor: "#fff", p: 4 }}>
                    <Typography variant="h3" gutterBottom sx={{ p: 0 }}>
                      {patient?.User?.FirstName} {patient?.User?.FamilyName}
                    </Typography>
                    <TabContext value={selectedTab}>
                      <Box
                        sx={{
                          borderBottom: 1,
                          borderColor: "divider",
                          display: "flex",
                          justifyContent: "center",
                        }}
                        width="100%"
                      >
                        <TabList
                          onChange={handleChangeTab}
                          textColor="secondary"
                          indicatorColor="secondary"
                          variant="scrollable"
                          scrollButtons="auto"
                          allowScrollButtonsMobile
                        >
                          <Tab label="General" value="0" />
                          <Tab label="Allergies" value="1" />
                          <Tab label="Chronic Diseases" value="2" />
                          <Tab label="Critic Notes" value="3" />
                        </TabList>
                      </Box>

                      <TabPanel value="0">
                        <PatientBody />
                      </TabPanel>
                      <TabPanel value="1">
                        <Allergies />
                      </TabPanel>
                      <TabPanel value="2">
                        <ChronicDiseases />
                      </TabPanel>
                      <TabPanel value="3">
                        <CriticNotes />
                      </TabPanel>
                      <Grid
                        container
                        sx={{
                          pt: 8,
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Grid item>
                          <Button
                            type="submit"
                            variant="contained"
                            color="secondary"
                            className={classes.button}
                            fullWidth                            
                          >
                            Save
                          </Button>
                        </Grid>
                      </Grid>
                    </TabContext>
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        ) : (
          <LinearProgress color="secondary" />
        )}
      </MainCard>
    </>
  );
};

export default EditPatient;
